import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation, Link } from "react-router-dom"
import moment from "moment"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Grid, Box, Typography, Tooltip } from "@material-ui/core"
import AttachmentIcon from "../../../icons/AttachamentIcon"
import { operationsHelper } from "../../../../helpers/operations"
import { getFileRowClickRequest, getFileRequestByPage } from "../../../../actions/documentModalActions"
import { getAnnotation, getAuditHistory, getChat, updateCurrentSession, updateViewerPageNumber } from "../../../../actions/viewerActions"
import Annotations from "../../additionalDetails/Annotations"
import { ANNO_BG_COLOR } from "../../../../utils"
import { getReviewBasedOnFileIdRequest } from "../../../../actions/reviewActions"
import { searchHighlightFilter } from "../../../../actions/highlightActions"
import { getInfo } from "../../../../actions/infoActions"

const useStyles = makeStyles((theme) => ({
	root: {
		...theme.typography.body2,
		backgroundColor: theme.palette.common.white,
		borderRadius: "5px",
		padding: "16px 23px",
		whiteSpace: "pre-line",
		maxHeight: "80vh",
		height: "65vh",
		overflow: "auto",
		// "& .mark_element.mark_date": { backgroundColor: theme.palette.grey.D700 },
		// "& .mark_element.mark_concept": { backgroundColor: theme.palette.grey.D900 },
		// "& .mark_element.mark_word": { backgroundColor: theme.palette.grey.D1000 },
		// "& .mark_element.mark_email": { backgroundColor: theme.palette.grey.D800 },

		//TODO: This is the temporary solution and we will get back to it when we have the changes ready from Library
		//https://github.com/react-pdf-viewer/react-pdf-viewer/pull/1625

		"& .mark_element.mark_date": { background: "#ffff00" },
		"& .mark_element.mark_concept": { background: "#ffff00" },
		"& .mark_element.mark_word": { background: "#ffff00" },
		"& .mark_element.mark_email": { background: "#ffff00" },
		"& .ellipses": { overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" },
	},
	emailAttachment: {
		...theme.typography.body2,
		// display: "flex",
		"& .MuiTypography-body2": {
			color: theme.palette.primary.dark,
			textDecoration: "underline",
			fontSize: theme.typography.caption.fontSize,
		},
	},
}))

const EmailViewer = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const history = useHistory()
	const theme = useTheme()
	const [t] = useTranslation("global")
	const location = useLocation()

	const { documentDetails, currentDocument } = useSelector((state) => state.documentModal)
	const highlightFilter = useSelector((state) => state.highlightFilter.search)
	const loadedConcept = useSelector((state) => state.highlightFilter.search.loadedConcept)
	const aiEnabled = useSelector((state) => state?.info?.version?.ai_enabled)

	const maskHightlight = (currentDocumentContent, isTitle) => {
		let markedText = operationsHelper.cleanHTMLTags(currentDocumentContent)
		if (highlightFilter?.emails) {
			markedText = operationsHelper.highlightEmails(markedText)
		}

		if (highlightFilter?.concepts) {
			markedText = operationsHelper.highlightConcepts(markedText, loadedConcept)
		}

		if (highlightFilter?.dates) {
			markedText = operationsHelper.hightlightDatesInDocument(markedText)
		}

		if (highlightFilter?.specificWord) {
			markedText = operationsHelper.highlightWords(markedText, highlightFilter?.searchWord)
		}

		if (isTitle) {
			return { __html: `<div class="ellipses">${markedText}</div>` }
		}

		return { __html: `<div className="mark_element">${markedText}</div>` }
	}

	const formatDate = (date) => {
		const formate = "DD/MM/YYYY, hh:mm:ss"
		return moment(date).format(formate)
	}

	return (
		<Box className={classes.root} id="viewer-container">
			<Grid container direction="column" spacing={1}>
				<Grid item xs={12} container>
					<Grid item xs={6}>
						<Tooltip title={documentDetails?.metadata?.subject || documentDetails?.name} placement="top-start">
							<Typography
								noWrap
								dangerouslySetInnerHTML={maskHightlight(documentDetails?.metadata?.subject || documentDetails?.name, true)}
							/>
						</Tooltip>
					</Grid>
					<Grid item xs={6}>
						<Typography
							variant="body2"
							style={{ fontSize: theme.typography.caption.fontSize, textAlign: "end" }}
							dangerouslySetInnerHTML={maskHightlight(formatDate(documentDetails?.metadata?.creation_time?.toString()))}
						/>
					</Grid>
				</Grid>
				<Grid item xs={12} container>
					<Grid item xs={1}>
						<Typography style={{ fontSize: theme.typography.caption.fontSize }}>{t("viewer.fileType.email.from")}:</Typography>
					</Grid>
					<Grid item xs={11}>
						<Typography
							variant="body2"
							style={{ fontSize: theme.typography.caption.fontSize }}
							dangerouslySetInnerHTML={maskHightlight(documentDetails?.metadata?.from?.toString())}
						/>
					</Grid>
				</Grid>
				<Grid item xs={12} container>
					<Grid item xs={1}>
						<Typography style={{ fontSize: theme.typography.caption.fontSize }}>{t("viewer.fileType.email.to")}:</Typography>
					</Grid>
					<Grid item xs={11}>
						<Typography
							variant="body2"
							style={{ fontSize: theme.typography.caption.fontSize }}
							dangerouslySetInnerHTML={
								documentDetails?.metadata?.to?.length > 0
									? maskHightlight(documentDetails?.metadata?.to?.toString())
									: { __html: "-" }
							}
						/>
					</Grid>
				</Grid>
				{documentDetails?.metadata?.cc?.length > 0 && (
					<Grid item xs={12} container>
						<Grid item xs={1}>
							<Typography style={{ fontSize: theme.typography.caption.fontSize }}>
								{t("viewer.fileType.email.cc")}:
							</Typography>
						</Grid>
						<Grid item xs={11}>
							<Typography
								variant="body2"
								style={{ fontSize: theme.typography.caption.fontSize }}
								dangerouslySetInnerHTML={
									documentDetails?.metadata?.cc?.length > 0
										? maskHightlight(documentDetails?.metadata?.cc?.toString())
										: { __html: "-" }
								}
							/>
						</Grid>
					</Grid>
				)}
				{documentDetails?.metadata?.bcc?.length > 0 && (
					<Grid item xs={12} container>
						<Grid item xs={1}>
							<Typography style={{ fontSize: theme.typography.caption.fontSize }}>
								{t("viewer.fileType.email.bcc")}:
							</Typography>
						</Grid>
						<Grid item xs={11}>
							<Typography
								variant="body2"
								style={{ fontSize: theme.typography.caption.fontSize }}
								dangerouslySetInnerHTML={
									documentDetails?.metadata?.bcc?.length > 0
										? maskHightlight(documentDetails?.metadata?.bcc?.toString())
										: { __html: "-" }
								}
							/>
						</Grid>
					</Grid>
				)}
				{documentDetails?.children_files?.length > 0 && (
					<Grid item xs={12} container>
						<Grid item xs={1}>
							<Typography>
								<AttachmentIcon />
							</Typography>
						</Grid>
						<Grid item xs={11}>
							<Box className={classes.emailAttachment}>
								<Grid container direction="column">
									{documentDetails?.children_files?.map((attachment, index) => {
										return (
											<Grid item xs={12}>
												<Typography variant="body2" noWrap>
													<Link
														to={{
															pathname: `${attachment?.file_id}`,
															search: window?.location?.search,
															state: { prevPath: location?.state?.prevPath },
														}}
														onClick={() => {
															dispatch(getFileRowClickRequest(attachment?.file_id))
															dispatch(
																getFileRequestByPage({
																	id: attachment?.file_id,
																	page: 1,
																	reviewId: props?.retainedFilterJobData?.additional_params?.review_id,
																})
															)
															dispatch(updateViewerPageNumber(1))
															dispatch(getAuditHistory(attachment?.file_id))
															dispatch(getAnnotation(attachment?.file_id))
															const chatPayload = {
																file_ids: [attachment?.file_id],
															}
															if (aiEnabled) {
																dispatch(getChat(chatPayload))
															}
															dispatch(updateCurrentSession(0))
															dispatch(getReviewBasedOnFileIdRequest(attachment?.file_id))
															const filterData = { ...highlightFilter }
															filterData["nativeView"] = !filterData["nativeView"]
															dispatch(searchHighlightFilter(filterData))
														}}
													>
														{attachment?.name}
													</Link>
												</Typography>
											</Grid>
										)
									})}
								</Grid>
							</Box>
						</Grid>
					</Grid>
				)}
				<Grid item xs={12} container>
					<Grid item xs={12}>
						{highlightFilter?.annotation ? (
							<>
								<Annotations
									id="viewer-container"
									style={{ ...theme.typography.body2 }}
									content={currentDocument?.toString()}
								/>
							</>
						) : (
							<>
								<Typography
									variant="body2"
									style={{ fontSize: theme.typography.body2.fontSize, marginTop: "10px" }}
									dangerouslySetInnerHTML={currentDocument ? maskHightlight(currentDocument?.toString()) : ""}
								/>
							</>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Box>
	)
}

export default EmailViewer
